import styled, { css } from 'styled-components';

import { Button, Panel, Text } from '../../../../components';
import {
  ANIM,
  COLORS,
  FIXED_HEADER,
  HEADER_DESKTOP_PX,
  HEADER_MOBILE_PX,
  HOME,
  MOBILE,
  MOBILE_NAV_ANIM,
  REDUCED_MOTION,
  SPACE,
  SORTABLE_ANIM,
} from '@/nova/theme/tokens';
import { MOBILE_NAV_DRAWER_OPEN, MOBILE_NAV_HIDDEN } from '@/nova/layouts/MainLayout/mobileNavVisibility';
import { scrollFadeMaskY, thinScrollbar } from '@/nova/theme/mixins';
import {
  FILTER_PIN_TOP,
  GAME_LISTS_LISTS_PX,
  GAME_LISTS_TREE_PX,
  HELD_HEAD_VAR,
} from '../../game-list-geometry';
import { PublishBtn } from '../CarouselCuration/CarouselCuration.styles';

/** The pinned area header above the tab with the Website hub on (the Website shell publishes its pinned box); nothing
 *  anywhere else (see GameListFilter, whose row pins under it too). */
const BACK_HEADER = 'var(--ows-back-header-offset, 0px)';

/* The data probe: the brand site loaded out of sight so its game lists can be read over the dsi bridge.
   Kept INSIDE the viewport (transparent, behind the page, not hit-testable) rather than parked off-screen,
   because browsers throttle cross-origin frames that sit outside the viewport. */
export const ProbeFrame = styled.iframe`
  position: fixed;
  right: 0;
  bottom: 0;
  width: 1280px;
  height: 900px;
  border: 0;
  opacity: 0;
  pointer-events: none;
  z-index: -1;
`;

/* The tab on a wide desktop: the site's pages as a tree in a column on the left, the open page beside it (its lists,
   then the open list). Narrower, and on a phone, one level at a time (see PagesLevel, LevelBack). */
export const Columns = styled.div<{ $tree: boolean }>`
  /* Brought back into view under the studio's top bar and the pinned area header when a page is opened from far down
     the tab, or a level drilled into or back out of (bringTabIntoView). */
  scroll-margin-top: calc(${HEADER_DESKTOP_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + ${BACK_HEADER});

  ${FIXED_HEADER} {
    scroll-margin-top: calc(
      ${HEADER_MOBILE_PX}px + var(--ows-imp-safe-top, env(safe-area-inset-top)) + var(--ows-imp-offset, 0px) +
        ${BACK_HEADER}
    );
  }

  ${({ $tree }) =>
    $tree
      ? css`
          display: grid;
          grid-template-columns: ${GAME_LISTS_TREE_PX}px minmax(0, 1fr);
          align-items: start;
          gap: ${SPACE[24]};
        `
      : css`
          display: flex;
          flex-direction: column;
          margin-top: ${SPACE[4]};
        `}
`;

/* The three columns' height on a wide desktop: from where they start down to the bottom of the screen, less the 24px the
   tab keeps there, as GameLists measures it (`--game-panes-h`); until it has, the pinned top's room. Each column scrolls
   on its own inside its frame, and the page does not. */
const PANE_HEIGHT = `var(--game-panes-h, calc(100vh - ${FILTER_PIN_TOP.desktop}px - ${SPACE[24]}))`;

/* The tree's column on a wide desktop: framed as the other two (the shared Panel, at the column's tighter padding),
   as tall as they are, its top level with theirs (see Body), scrolling on its own when the site has more pages than it
   holds. */
export const TreeColumn = styled(Panel)`
  min-width: 0;
  height: ${PANE_HEIGHT};
  margin-top: ${SPACE[24]};
  padding: ${SPACE[20]} ${SPACE[16]};
  overflow-y: auto;
  ${thinScrollbar}
`;

export const MainColumn = styled.div`
  min-width: 0;
`;

/* Drilled down, the site's pages: the page tree at the tab's width, framed as its column is on a wide desktop. */
export const PagesLevel = styled(Panel)`
  margin-top: ${SPACE[24]};
  padding: ${SPACE[20]} ${SPACE[16]};
`;

/* Drilled down, the way back up a level: to the pages from a page's lists, to the page's lists from a list's games. Its
   arrow's tip sits under the tip of the area's back arrow over the tab (the user, 2026-10-19): BackHeader centres that
   arrow, drawn 20px wide, in its 32px button at the tab's left edge, so its tip is 6px in (off the scale on purpose),
   and this arrow's tip is its glyph's left edge. */
export const LevelBack = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[8]};
  max-width: calc(100% - 6px);
  min-width: 0;
  margin-top: ${SPACE[16]};
  margin-left: 6px;
  padding: ${SPACE[8]} ${SPACE[0]};
  border: none;
  background: transparent;
  color: ${COLORS.textMuted};
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
  transition: color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
  }

  svg {
    flex: none;
  }

  > span {
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const fadeMs = ANIM.PUBLISH_ALL_FADE_MS;
const foldMs = ANIM.PUBLISH_ALL_FOLD_MS;

/* Publish changes' place: at the right of the area header's row, beside "Game Lists" (in the page's column of
   lists, under Add list, where the tab has no such header). Fades in when there is something to
   publish on the page and out when there is not. Hidden, it keeps its place and takes no clicks; `visibility` switches
   only once the fade out has run, so it is seen to go. Drilled down (`$fold`: on a phone, and on a desktop too narrow
   for the columns, in the column; on a phone, in the header, where it has a row of its own) it keeps no place while away
   (the user, 2026-10-06): once faded out it folds shut, its height and, in the column, the 16px above it (its negative
   margin), so the lists follow the page's name; it opens again before it fades in. */
export const ChangeBar = styled.div<{ $shown: boolean; $fold: boolean; $inColumn?: boolean; $spans?: boolean }>`
  display: flex;
  /* Publish changes, shrinking into its check once published, shrinks to its right. */
  justify-content: flex-end;
  /* In a phone's area header, across the action's slot, so Publish changes can open across it (to its right edge). */
  ${({ $spans }) => ($spans ? 'flex: 1 1 auto; min-width: 0; justify-content: flex-end;' : '')}
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  visibility: ${({ $shown }) => ($shown ? 'visible' : 'hidden')};
  pointer-events: ${({ $shown }) => ($shown ? 'auto' : 'none')};
  transition:
    opacity ${fadeMs}ms ${ANIM.EASE},
    visibility 0s linear ${({ $shown }) => ($shown ? 0 : fadeMs)}ms;

  ${({ $fold, $shown, $inColumn }) =>
    $fold
      ? css`
          max-height: ${$shown ? SPACE[48] : '0'};
          margin-top: ${$shown || !$inColumn ? '0' : `-${SPACE[16]}`};
          overflow: ${$shown ? 'visible' : 'hidden'};
          transition: ${$shown
            ? `max-height ${foldMs}ms ${ANIM.EASE}, margin-top ${foldMs}ms ${ANIM.EASE},
              opacity ${fadeMs}ms ${ANIM.EASE} ${foldMs}ms, visibility 0s linear 0ms`
            : `opacity ${fadeMs}ms ${ANIM.EASE}, visibility 0s linear ${fadeMs}ms,
              max-height ${foldMs}ms ${ANIM.EASE} ${fadeMs}ms, margin-top ${foldMs}ms ${ANIM.EASE} ${fadeMs}ms`};
        `
      : ''}

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The open page, beside the page tree: on a wide desktop its column of lists and the open list's column beside it
   (`$split`); drilled down, one of the two. Its parts are 24px from each other (the design's rhythm), the first 24px
   down, where the tree's own top sits (see TreeColumn). */
export const Body = styled.div<{ $split?: boolean; $tight?: boolean }>`
  margin-top: ${SPACE[24]};

  /* On a phone, the open list's level under the area header that names it: its row (the filter and the actions) as far
     under the name as the list is under the row (the user, 2026-10-21), the header's fade tail, the page's gap and the
     column's 4px making up the room; where the row pins when held (ACTIONS_ROW_PIN_TOP.phone), so it does not move as
     it starts to hold. */
  ${({ $tight }) =>
    $tight &&
    css`
      margin-top: 0;
    `}

  ${({ $split }) =>
    $split
      ? css`
          display: grid;
          grid-template-columns: ${GAME_LISTS_LISTS_PX}px minmax(0, 1fr);
          align-items: start;
          gap: ${SPACE[24]};
        `
      : css`
          display: flex;
          flex-direction: column;
          gap: ${SPACE[24]};
        `}
`;

/* The open list's column on a wide desktop: framed as the other two, as tall, its notes, filter row and games scrolling
   inside it (see GamesScroll), the filter row pinned at its top as a sheet's is. */
export const GamesPane = styled(Panel)`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: ${PANE_HEIGHT};
  padding: 0;
  overflow: hidden;
`;


/** How far under the open list's heading its blur fades out: the gap the column keeps under it, so at rest it reaches
 *  none of the games. */
export const HEAD_FADE_PX = 16;

/* What scrolls in the open list's column, from the top of its frame: its heading first, then the games, the frame's own
   padding at the sides so the scrollbar runs at the frame's edge. The heading's height and its fade are published on it
   (HELD_HEAD_VAR, see GameLists), so a searched-for game lands under the heading, not behind it. */
export const GamesScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: ${HEAD_FADE_PX}px;
  padding: 0 ${SPACE[16]} ${SPACE[20]};
  overflow-y: auto;
  ${thinScrollbar}
`;

/* Under a column's held head or footer: a see-through layer of the column's surface that blurs what passes under it,
   fading out past it (by the gap the column keeps there, so at rest it reaches nothing): below a head, above a footer. */
const blurLayer = (above: boolean) => css`
  &::before {
    content: '';
    position: absolute;
    inset: ${above ? `-${HEAD_FADE_PX}px 0 0` : `0 0 -${HEAD_FADE_PX}px`};
    z-index: -1;
    background: color-mix(in srgb, ${COLORS.surfaceCard} 80%, transparent);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    ${scrollFadeMaskY(above, !above, HEAD_FADE_PX)}
    pointer-events: none;
  }
`;

/* A column's head held at the top of its frame as what is under it scrolls up there, blurring it. Inset as the column's
   content is. */
const heldHead = css`
  position: sticky;
  top: 0;
  z-index: 3;
  flex: none;
  margin: 0 -${SPACE[16]};
  padding: ${SPACE[20]} ${SPACE[16]} 0;
  ${blurLayer(false)}
`;

/* The open list's heading (its name, its search, its actions), held so as the games scroll up under it. Inset as the
   column of lists' content is (ListsColumn), so the two names and counts stand level. */
export const GamesHead = styled.div`
  ${heldHead}
`;

/* The page's lists: its name and how many lists it shows, a search, Add list, then a row for each list and the info point
   last. The shared Panel, the frame Blog and Logos use, at a tighter padding for the narrow column. On a wide desktop
   (`$pane`) as tall as the columns beside it, its rows scrolling under its head (see ListRowsScroll). */
export const ListsColumn = styled(Panel)<{ $pane: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-width: 0;
  padding: ${SPACE[20]} ${SPACE[16]};

  ${({ $pane }) =>
    $pane
      ? css`
          height: ${PANE_HEIGHT};
          overflow: hidden;
        `
      : css`
          /* Its footer's surface, drawn to the frame's edges, rounded at its bottom corners as the frame is (the user,
             2026-10-21): clipped, not hidden, so the frame is no scroll area and the footer stays held to the screen. */
          overflow: clip;
        `}

  ${MOBILE} {
    padding: ${SPACE[20]} ${SPACE[16]};
  }
`;

/* The rows and the info point: in a column as tall as the screen, what scrolls when the page has more lists than it holds. */
export const ListRowsScroll = styled.div<{ $pane: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
  min-width: 0;

  ${({ $pane }) =>
    $pane
      ? css`
          flex: 1 1 auto;
          min-height: 0;
          /* From the top of the frame to its bottom: its head first (ListsTop), its footer last (ListsFooter), the
             frame's padding at the sides and under the rows. */
          margin: -${SPACE[20]} -${SPACE[16]};
          padding: 0 ${SPACE[16]} ${SPACE[20]};
          overflow-y: auto;
          ${thinScrollbar}
        `
      : ''}
`;

/* The column's head on a wide desktop: held at the top of the frame as the rows scroll up under it, blurring them. */
export const ListsTop = styled.div`
  ${heldHead}
  display: flex;
  flex-direction: column;
  gap: ${SPACE[16]};
`;

/** The mobile bottom navigation's pill, from the bottom of the screen: 20px under it and the pill itself (8px round an
 *  item of a 24px icon, its label and their padding, about 60px). */
const MOBILE_NAV_PILL_TOP_PX = 80;
/** How far over the pill the held footer's button rests (the user, 2026-10-20: closer than the page's clearance). */
const FOOTER_OVER_PILL_PX = 12;

/** Create a new list's height (AddListBtn): the phone's tap target under MOBILE. */
const ADD_LIST_PX = 40;
const ADD_LIST_PHONE_PX = 48;

/* Drilled down on a phone: held at the bottom of the screen as the page scrolls, just over the bottom navigation while it
   shows, following it down while it is scrolled away. */
const heldOverPill = css`
  bottom: calc(${MOBILE_NAV_PILL_TOP_PX}px + env(safe-area-inset-bottom));
  transition: bottom ${MOBILE_NAV_ANIM.hideMs}ms ${ANIM.EASE};

  ${MOBILE_NAV_HIDDEN} & {
    bottom: env(safe-area-inset-bottom);
  }

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The column's footer, Create a new list, last of what scrolls (the rows, see ListRowsScroll): held at the bottom of the
   frame as the rows scroll under it, blurring them, the button as far from the frame's bottom edge as from its sides
   (the user, 2026-10-20). Drilled down it is held at the bottom of the screen as the page scrolls (heldOverPill). */
export const ListsFooter = styled.div<{ $pane?: boolean }>`
  position: sticky;
  /* A held box keeps clear of its scroll area's padding: on a wide desktop held over the padding under the rows, at the
     frame's bottom edge. */
  bottom: ${({ $pane }) => ($pane ? `-${SPACE[20]}` : '0')};
  z-index: 3;
  flex: none;
  /* Right after the last row, and held at the frame's bottom only once the rows run past it (the user, 2026-10-23: at
     the frame's bottom under a few rows, it stood far from them). */
  /* Over the frame's padding at the sides and under it, the button inset from its edges alike. */
  margin-right: -${SPACE[16]};
  margin-bottom: -${SPACE[20]};
  margin-left: -${SPACE[16]};
  padding: 0 ${SPACE[16]} ${SPACE[16]};
  ${blurLayer(true)}

  ${FIXED_HEADER} {
    /* The button alone, 12px over the pill: over the navigation's scrim (its z-index 50), which tinted it (the user,
       2026-10-21), its surface under it (ListsFooterSurface); under the "More" drawer's overlay while that shows. */
    ${heldOverPill}
    margin-top: 0;
    padding-bottom: ${FOOTER_OVER_PILL_PX}px;
    z-index: 51;

    &::before {
      display: none;
    }

    ${MOBILE_NAV_DRAWER_OPEN} & {
      z-index: 2;
    }
  }
`;

/* On a phone, the footer's surface apart from its button: under the bottom navigation's scrim as the rest of the column
   is, only the button over it (the user, 2026-10-21). Held as the footer is and as tall, the footer drawn on it (pulled up
   over it by its height and the rows' gap). */
export const ListsFooterSurface = styled.div`
  display: none;

  ${FIXED_HEADER} {
    display: block;
    position: sticky;
    ${heldOverPill}
    z-index: 2;
    flex: none;
    margin: auto -${SPACE[16]} calc(-${ADD_LIST_PX + FOOTER_OVER_PILL_PX}px - ${SPACE[16]});
    height: ${ADD_LIST_PX + FOOTER_OVER_PILL_PX}px;
    pointer-events: none;
    ${blurLayer(true)}
  }

  ${MOBILE} {
    margin-bottom: calc(-${ADD_LIST_PHONE_PX + FOOTER_OVER_PILL_PX}px - ${SPACE[16]});
    height: ${ADD_LIST_PHONE_PX + FOOTER_OVER_PILL_PX}px;
  }
`;

/* The column's head: the page's name over how many lists it shows. */
export const ListsHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${SPACE[12]};
`;

export const ListsTitles = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

/** The page's name and its count of lists set their lines in px, so the skeleton's placeholders stand on lines as tall
 *  whatever the font (see GameListsSkeleton). */
export const LISTS_TITLE_LINE_PX = 24;
export const LISTS_META_LINE_PX = 16;

export const ListsTitle = styled(Text)`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: ${LISTS_TITLE_LINE_PX}px;
  color: ${COLORS.heading};
  overflow-wrap: anywhere;
`;

export const ListsMeta = styled(Text)`
  font-size: 13px;
  line-height: ${LISTS_META_LINE_PX}px;
  color: ${COLORS.textMuted};
`;

/* The rows, one under another, close together. */
export const ListRows = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  margin: 0;
  padding: 0;
  list-style: none;
`;

/* A list's row, with what the page tells screen readers beside it, moved by the sorting hook as the games' rows are
   (useSortableRows: its transform an inline style, eased as the others make way and as it settles). Press and hold is
   the drag's gesture: no text selection, no callout. The row in the hand is lifted over the others. (A list the page
   hides is drawn by the row itself, muted with its crossed-out eye.) */
export const RowWrap = styled.li<{ $dragged: boolean; $animated: boolean }>`
  position: relative;
  border-radius: 8px;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  overflow-anchor: none;
  transition: ${({ $animated, $dragged }) =>
    $animated
      ? `transform ${$dragged ? SORTABLE_ANIM.SETTLE_MS : SORTABLE_ANIM.SHIFT_MS}ms ${SORTABLE_ANIM.EASE},
         box-shadow ${SORTABLE_ANIM.SETTLE_MS}ms ${SORTABLE_ANIM.EASE}`
      : 'none'};

  ${({ $dragged }) =>
    $dragged &&
    css`
      /* Over the column's held head and footer too (ListsTop, ListsFooter: z-index 3), so it does not pass under
         them as the rows scroll near them. */
      z-index: 4;
      background: ${HOME.menuBg};
      box-shadow: ${COLORS.menuShadow};

      ${FIXED_HEADER} {
        /* On a phone the footer's button is over the bottom navigation (z-index 51), its surface under it (2). */
        z-index: 52;
      }
    `}
`;

/* The info point, at the right of the column's head, level with the page's name: a quiet round button holding its icon
   and the X it turns into while its note is open (see InfoPointGlyph), lit then. */
export const InfoPointBtn = styled.button`
  flex: none;
  position: relative;
  width: 28px;
  height: 28px;
  /* Centred on the name's 24px line. */
  margin-top: -2px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.textMuted};
  cursor: pointer;
  transition:
    border-color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE},
    color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover,
  &[aria-expanded='true'] {
    border-color: ${COLORS.textFaint};
    color: ${COLORS.text};
  }

  &:focus-visible {
    outline: 2px solid ${COLORS.accent};
    outline-offset: 2px;
  }
`;

/* One of the info point's two glyphs, the icon or the X, laid over the other: the one leaving turns away and fades
   as the other turns in. */
export const InfoPointGlyph = styled.span<{ $shown: boolean }>`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transform: ${({ $shown }) => ($shown ? 'rotate(0deg) scale(1)' : 'rotate(-90deg) scale(0.5)')};
  transition:
    opacity ${ANIM.INFO_POINT_TURN_MS}ms ${ANIM.EASE},
    transform ${ANIM.INFO_POINT_TURN_MS}ms ${ANIM.EASE};

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* The info point's note, under the column's head: it folds open (growing from nothing) and fades in, and folds shut and
   fades out, staying laid out until it has, then hidden. Shut, it takes no room, the head's gap under it included (see
   InfoPointBox). */
export const InfoPointNote = styled.div<{ $open: boolean }>`
  min-width: 0;
  margin-top: -${SPACE[16]};
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? '1fr' : '0fr')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition:
    grid-template-rows ${ANIM.INFO_POINT_FOLD_MS}ms ${ANIM.EASE},
    opacity ${ANIM.INFO_POINT_FADE_MS}ms ${ANIM.EASE},
    visibility 0s linear ${({ $open }) => ($open ? 0 : ANIM.INFO_POINT_FOLD_MS)}ms;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* What folds: clipped to the row's height as it grows and shrinks. */
export const InfoPointFold = styled.div`
  min-height: 0;
  overflow: hidden;
`;

/* The note's box: an info callout's inset surface, hairline border and corners, holding its copy alone (the icon
   beside it already says what it is). */
export const InfoPointBox = styled.div`
  /* The head's gap, inside what folds. */
  margin-top: ${SPACE[16]};
  padding: ${SPACE[12]} ${SPACE[16]};
  border-radius: 12px;
  border: 1px solid ${COLORS.panelBorder};
  background: ${COLORS.surfaceInset};
`;

export const InfoPointText = styled(Text)`
  display: block;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${COLORS.text};
`;

/* Add list, on a line of its own under the page's name: the shared outlined button with its plus and its label,
   across the column as Publish changes is under it, as tall as a list's row (48px on a phone, Button's own tap
   target there). */
export const AddListBtn = styled(Button)`
  flex: none;
  height: ${ADD_LIST_PX}px;
  font-size: 13px;
  font-weight: 700;

  ${MOBILE} {
    height: ${ADD_LIST_PHONE_PX}px;
  }
`;

/* Stacked notes sit 16px apart: Body's 24px gap less the 8px a note is pulled up; in the open list's column, its own
   16px gap. */
export const Notes = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: -${SPACE[8]};

  ${GamesScroll} > & {
    margin-top: 0;
  }
`;

/* A muted one-line explanation led by an info glyph. */
export const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${COLORS.textMuted};

  svg {
    flex: 0 0 auto;
    margin-top: 2px;
  }
`;

export const NoteText = styled(Text).attrs({ tag: 'span' as const })``;

/* "Reset game order" (and "Try again"): a quiet hairline pill, secondary to Preview + Publish. */
export const ResetBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 16px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  font-weight: 650;
  cursor: pointer;
  transition: border-color ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

  &:hover:not(:disabled) {
    border-color: ${COLORS.textFaint};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }
`;

export const RetryBtn = styled(ResetBtn)`
  margin-top: 12px;
`;

/** How long a phone's Publish changes takes to open across the header and to close back (as the filter folds), and
 *  how long it takes, once published, to shrink to its right into its check. */
export const PUBLISH_OPEN_MS = 240;
/** How long the check stays, clear, before the bar fades out (the user, 2026-10-21). */
export const PUBLISH_CHECK_HOLD_MS = 500;
/** How long the bar takes to fade out. */
export const PUBLISH_FADE_MS = fadeMs;

/* Publish changes: the list's own Publish, at the column's width with its label centred; on a phone's area header, its
   icon alone (`$iconOnly`), a round button on the title's row, which a first press opens across the row with its label
   (`$armed`, the user, 2026-10-21) and a second press publishes. */
export const PublishAllBtn = styled(PublishBtn)<{ $iconOnly?: boolean; $armed?: boolean; $done?: boolean }>`
  flex: 1 1 auto;
  justify-content: center;

  /* Published: it shrinks to its right into a round check (its width set on it, from what it was to 40px), which stays
     a moment, clear, before the bar fades (the user, 2026-10-21). */
  ${({ $done }) =>
    $done &&
    css`
      flex: none;
      height: 40px;
      padding: 0;
      gap: 0;
      border-radius: 999px;
      overflow: hidden;
      white-space: nowrap;
      transition:
        width ${PUBLISH_OPEN_MS}ms ${ANIM.EASE},
        padding ${PUBLISH_OPEN_MS}ms ${ANIM.EASE};

      &:disabled {
        opacity: 1;
      }

      ${REDUCED_MOTION} {
        transition: none;
      }
    `}

  ${({ $iconOnly, $armed }) =>
    $iconOnly &&
    css`
      flex: none;
      width: ${$armed ? '100%' : '40px'};
      height: 40px;
      padding: 0 ${$armed ? SPACE[16] : '0'};
      gap: ${$armed ? SPACE[8] : '0'};
      border-radius: 999px;
      overflow: hidden;
      white-space: nowrap;
      transition:
        width ${PUBLISH_OPEN_MS}ms ${ANIM.EASE},
        padding ${PUBLISH_OPEN_MS}ms ${ANIM.EASE},
        gap ${PUBLISH_OPEN_MS}ms ${ANIM.EASE},
        filter ${ANIM.CREATIVE_HOVER_MS}ms ${ANIM.EASE};

      & > svg {
        flex: none;
      }

      ${REDUCED_MOTION} {
        transition: none;
      }
    `}
`;

/* Publish changes' label: on a phone none while it is its icon alone, fading in as it opens across the header; gone as
   it shrinks into its check once published. */
export const PublishLabel = styled.span<{ $shown: boolean }>`
  max-width: ${({ $shown }) => ($shown ? '320px' : '0')};
  overflow: hidden;
  opacity: ${({ $shown }) => ($shown ? 1 : 0)};
  transition:
    max-width ${PUBLISH_OPEN_MS}ms ${ANIM.EASE},
    opacity ${PUBLISH_OPEN_MS / 2}ms ${ANIM.EASE} ${({ $shown }) => ($shown ? PUBLISH_OPEN_MS / 2 : 0)}ms;

  ${REDUCED_MOTION} {
    transition: none;
  }
`;

/* Drilled down on a phone, a list with no games drawn: its actions, at the right, where the heading the header took
   carried them. */
export const LevelActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

/* Text for screen readers only (the unpublished dot's meaning, which sighted owners get from the row's tooltip). */
export const ScreenReaderText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

